import React, { Fragment } from 'react';
import { Cell } from '@tanstack/react-table';
import { Link } from 'react-router';
import { Tooltip } from '@entur/tooltip';
import classNames from 'classnames/bind';
import { parseCellValue, parseLink } from '../cell-renderers/parseCellValue';
import { DrawerConfig } from '../types';
import { type CleosTableFeaturesType } from '../tableFeatures';
import styles from '../styles.module.scss';

const cx = classNames.bind(styles);

interface TableCellProps<TData extends object> {
  cell: Cell<CleosTableFeaturesType, TData, unknown>;
  cellIndex: number;
  original: TData;
  depth: number | null;
  rowType: boolean;
  expandable?: boolean;
  onExpandToggle: () => void;
  openDrawer: (row: TData, drawer: DrawerConfig, index?: number) => void;
}

export const TableCell = <TData extends object>({
  cell,
  cellIndex,
  original,
  depth,
  rowType,
  expandable,
  onExpandToggle,
  openDrawer,
}: TableCellProps<TData>) => {
  const meta = cell.column.columnDef.meta;
  if (!meta) return null;

  const rawValue = cell.getValue();
  const cellValue = parseCellValue(rawValue, meta, original);

  const cellClasses = cx({
    td: true,
    number: meta.number || meta.amount || !!meta.format,
    icon: !!meta.icon || !!meta.checkbox,
    containsError: meta.containsError ? meta.containsError(original) : false,
    truncate: meta.truncate,
    underline: meta.underline,
    noWrap: meta.noWrap,
    multipleLines: meta.multipleLines,
  });

  const cellContentClasses = cx({ onClickCell: !!meta.onClick || !!meta.drawer });

  const typedOriginal = original as Record<string, unknown>;
  const tooltip =
    meta.tooltip && !meta.tooltip.noBody
      ? {
          content: meta.tooltip.text || typedOriginal[meta.tooltip.prop ?? ''],
          placement: meta.tooltip.placement,
        }
      : null;

  const linkTo = parseLink(meta.link, rawValue, original);
  const linkProps = linkTo ? { to: linkTo, state: meta.link?.state } : null;

  const { drawer, onClick } = meta;
  const onCellClick = drawer
    ? (_e: React.MouseEvent, idx?: number) => openDrawer(original, drawer, idx)
    : onClick
      ? (e: React.MouseEvent) => onClick(e, original)
      : null;

  /** Native title text for truncated cells; falls back to the raw value when the renderer returns JSX. */
  const getTitle = (value: React.ReactNode): string | undefined => {
    if (!meta.truncate || tooltip) return undefined;
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof rawValue === 'string' || typeof rawValue === 'number') return String(rawValue);
    return undefined;
  };

  const isFirstCell = cellIndex === 0;
  const depthPaddingLeft = isFirstCell && depth ? `calc(10px + ${depth}rem)` : undefined;

  const renderContent = (value: React.ReactNode, idx?: number) => (
    <span
      onClick={onCellClick ? (e) => onCellClick(e, idx) : undefined}
      className={cellContentClasses}
      style={
        isFirstCell
          ? {
              color: rowType ? 'var(--cleos-info)' : undefined,
              fontWeight: depth === 0 ? 'bold' : undefined,
            }
          : undefined
      }
      title={getTitle(value)}
    >
      {linkProps ? <Link {...linkProps}>{value}</Link> : value}
    </span>
  );

  const wrappedContent = tooltip ? (
    <Tooltip content={String(tooltip.content ?? '')} placement={(tooltip.placement as never) || 'top'}>
      {renderContent(cellValue)}
    </Tooltip>
  ) : drawer?.multiple ? (
    String(cellValue ?? '')
      .split(',')
      .map((part, i, parts) => (
        <Fragment key={part.trim()}>
          {renderContent(part.trim(), i)}
          {i !== parts.length - 1 && ','}
        </Fragment>
      ))
  ) : (
    renderContent(cellValue)
  );

  return (
    <td
      className={cellClasses}
      style={depthPaddingLeft ? { paddingLeft: depthPaddingLeft } : undefined}
      onClick={(e) => {
        if (!onCellClick && expandable) {
          e.stopPropagation();
          onExpandToggle();
        }
      }}
    >
      {wrappedContent}
    </td>
  );
};
